import * as React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Button, Form, FormGroup, FormControl, HelpBlock, Popover } from 'react-bootstrap';
import PropTypes from 'prop-types';

import ActionsMenu from './ActionsMenu';
import { updateList, showBannerMessage, clearBannerMessage } from '../../actions/actions';
import { checkForDuplicate } from '../../helpers/utility';
import { MAX_LIST_NAME_LENGTH } from '../../global-constants'
import './styles/CurrentList.scss';

export class CurrentListClass extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			isEditingListName: false,
			showPopover: false,
			originalListName: '',
			newListName: '',
		}
	}

	componentDidUpdate(prevProps) {
		// Update input field value if a new list has been selected
		if (this.props.selectedList.name !== prevProps.selectedList.name) {
			const nameElement = document.getElementById('current_list_name');
			nameElement.value = this.props.selectedList.name;
		}
	}

	editListName = () => {
		const { selectedList } = this.props;

		this.setState({
			isEditingListName: true,
			originalListName: selectedList.name,
		}, () => {
			const nameElement = document.getElementById('current_list_name');
			nameElement.focus();
			nameElement.select();
		});
	};

	onKeyDown = e => {
		if (e.key === 'Enter') {
			this.updateListName()
			e.currentTarget.blur();
			e.preventDefault();
			return false;
		} else if (e.key === 'Escape') {
			this.setState({newListName: ''}, () => {this.updateListName()} );
			e.preventDefault();
			e.currentTarget.blur();
			e.preventDefault();
			return false;
		}
	}

	updateListName = () => {
		const { newListName, originalListName } = this.state;
		const { allLists, setBanner, clearBanner, selectedList } = this.props;
		const nameElement = document.getElementById('current_list_name');

		clearBanner();

		// Length should be restricted by input field maxlength, but ensure it here
		if (newListName.length > 0 && newListName.length <= MAX_LIST_NAME_LENGTH && newListName !== originalListName) {
			if (checkForDuplicate(allLists, newListName, setBanner)) {
				// Name is not a duplicate, update and exit edit mode
				this.props.updateList(selectedList.id, newListName)
				this.setState({isEditingListName: false, showPopover: false});
			} else {
				// Name is a duplicate, continue editing
				nameElement.focus();
			}
		} else {
			// Reset value to original
			nameElement.value = originalListName;
			this.setState({isEditingListName: false, showPopover: false});
		}
	}

	validationState() {
		const { newListName, showPopover } = this.state;
		const length = newListName.length;
		if (length === MAX_LIST_NAME_LENGTH) {
			if (!showPopover) {
				this.setState({showPopover: true});
			}
		} else if (showPopover) {
			this.setState({showPopover: false});
		}

		return null;
	}

	render() {
		const { selectedList, i18n, tree, addRemoveItem , checkForEmptyList} = this.props;
		const { isEditingListName, newListName, showPopover } = this.state;
		const showEditButtons = isEditingListName ? 'show' : 'hide';
		const editable = !!(selectedList.id && selectedList.id.length > 0);
		return (
			<div id="work_header" className={this.state.isEditingListName ? 'editing' : ''}>
				{/* Background captures clicks outside of form */}
				<div id="capture_blur" onClick={() => this.updateListName()}></div>
				<div id='current_list'>
					<Form inline>
						<FormGroup
							controlId="current_list_name"
							validationState={this.validationState()}
						>
							<FormControl
								type="text"
								maxLength={MAX_LIST_NAME_LENGTH}
								defaultValue={editable ? selectedList.name : ''}
								placeholder={editable ? '' : i18n.t('NO_LIST_SELECTED')}
								onChange={e => this.setState({newListName: e.currentTarget.value})}
								onKeyDown={e => this.onKeyDown(e)}
								onFocus={e => {
									if (!this.state.isEditingListName) {
										e.stopPropagation();
										e.currentTarget.blur();
									}
								}}
							/>
							{/* <FormControl.Feedback /> */}
							{/* <HelpBlock>Validation is based on string length.</HelpBlock> */}
						</FormGroup>
						<Popover
							hidden={!showPopover || !isEditingListName}
							id="popover-basic"
							placement="bottom"
						>
							{i18n.t('MAXIMUM_LIST_NAME_LENGTH', {length: MAX_LIST_NAME_LENGTH})}
						</Popover>
						<Button
							bsStyle='primary'
							className={`${showEditButtons}`}
							onClick={() => {
								this.updateListName();
							}}
						>
							{i18n.t('RENAME')}
						</Button>
						<Button
							bsStyle='link'
							className={`${showEditButtons}`}
							onClick={() => {
								this.setState({newListName: ''}, () => {this.updateListName()} );
							}}
						>
							{i18n.t('CANCEL')}
						</Button>
					</Form>
					<div className={`dots ${editable && !isEditingListName ? 'show' : 'hide'}`}>
						<ActionsMenu
							rename={this.editListName}
							addRemoveItem={addRemoveItem}
							checkForEmptyList={checkForEmptyList}
							tree={tree}
						/>
					</div>
				</div>
			</div>
		);
	}
}

CurrentListClass.propTypes = {
	allLists: PropTypes.array.isRequired,
	setBanner: PropTypes.func.isRequired,
	clearBanner: PropTypes.func.isRequired,
	updateList: PropTypes.func.isRequired,
	addRemoveItem: PropTypes.func.isRequired,
	checkForEmptyList: PropTypes.func.isRequired,
	tree: PropTypes.shape({}).isRequired,
	selectedList: PropTypes.shape({
		name: PropTypes.string,
	}),
	i18n: PropTypes.shape({}).isRequired,
};

CurrentListClass.defaultPropTypes = {
	selectedList: null,
};

const mapDispatchToProps = dispatch => ({
	updateList: (id, name) => {
		const cleanedName = name.trim();
		dispatch(updateList(id, {name: cleanedName}))
	},
	setBanner: (message, messageType) => { dispatch(showBannerMessage(message, messageType)) },
	clearBanner: () => { dispatch(clearBannerMessage()) },
});

const mapStateToProps = (state) => ({
	selectedList: state.selectedList,
	allLists: state.allLists,
});

const CurrentList = connect(
	mapStateToProps,
	mapDispatchToProps
)(CurrentListClass);

export default withTranslation()(CurrentList);
